import { memo, useMemo } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { cn } from '@lyearn/style';
import { ToolbarOption } from '@/components/Editor/PlainEditor/types';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { hideImageSeparator } from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageInput/styles';
import PraiseMessage from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage';
import {
  PRAISE_MESSAGE_EXTENSIONS,
  PRAISE_MESSAGE_PLUGINS,
} from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage/consts';
import { PRAISE_MESSAGE_EDITOR_DEFAULT_OPTIONS } from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage/consts';
import { editorStyles } from '@/modules/praises/views/PraiseWall/components/GivePraiseModal/components/PraiseMessage/styles';
import { MentionInputType } from '@/types/schema';

import { suggestionsContainerStyles } from '../../../SuggestionsContainer/styles';
import { TOOLBAR_ADD_AI_CONTENT, TOOLBAR_AI_DISCLAIMER } from '../../consts';
import { AIBaseEditorProps, AIPraiseMessageProps } from '../../types';
import AIBaseEditor from '../AIBaseEditor';
import { AI_PRAISE_MESSAGE_EDITOR_OPTION_MAP } from './consts';

function AIPraiseMessage({
  value,
  onChange,
  medias,
  error,
  setFieldValue,
  placeholder,
  editorOptions = PRAISE_MESSAGE_EDITOR_DEFAULT_OPTIONS,
  topic,
  onTopicError,
  textUseCase,
}: AIPraiseMessageProps) {
  const { [FeatureFlags.EnableLyearnAI]: enableLyearnAI } = useFeatureFlags();

  const onChangeText = useEvent((text: string) => {
    onChange(text);
  });

  const onChangeMedias = useEvent((medias: EntityMediaFragment[]) => {
    setFieldValue('medias', medias, true);
  });

  const onChangeMentions = useEvent((mentions: MentionInputType[]) => {
    setFieldValue('mentions', mentions, true);
  });

  const EDITOR_CLASSES = useMemo<AIBaseEditorProps['classes']>(
    () => ({
      root: 'rounded-8 border focus-within:border-focus-primary',
      rootActiveAI: cn(
        'border-dashed border-bg-primary focus-within:!border-bg-primary',
        suggestionsContainerStyles,
      ),
      editor: editorStyles,
    }),
    [],
  );

  const EDITOR_CORE_PROPS = useMemo<AIBaseEditorProps['editorCoreProps']>(
    () => ({ variant: 'body-short-01' }),
    [],
  );

  const EDITOR_OPTIONS_WITH_AI = useMemo(() => {
    return [
      TOOLBAR_ADD_AI_CONTENT,
      ToolbarOption.Separator,
      ...editorOptions,
      ToolbarOption.Spacer,
      TOOLBAR_AI_DISCLAIMER,
    ];
  }, [editorOptions]);

  if (enableLyearnAI) {
    return (
      <AIBaseEditor
        enableMentions
        className={cn(error ? 'border-border-error-dark' : 'border-field', hideImageSeparator)}
        classes={EDITOR_CLASSES}
        editorCoreProps={EDITOR_CORE_PROPS}
        editorOptionMap={AI_PRAISE_MESSAGE_EDITOR_OPTION_MAP}
        editorOptions={EDITOR_OPTIONS_WITH_AI}
        extensions={PRAISE_MESSAGE_EXTENSIONS}
        medias={medias}
        placeholder={placeholder}
        plugins={PRAISE_MESSAGE_PLUGINS}
        textUseCase={textUseCase}
        topic={topic}
        value={value}
        onChangeMedias={onChangeMedias}
        onChangeMentions={onChangeMentions}
        onChangeText={onChangeText}
        onTopicError={onTopicError}
      />
    );
  }

  return (
    <PraiseMessage
      {...{
        value,
        onChange,
        medias,
        error,
        setFieldValue,
        placeholder,
        editorOptions,
      }}
    />
  );
}

export default memo(AIPraiseMessage);
